import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  FileText,
  Calendar,
  Tag,
  X,
  Save,
  Eye,
  EyeOff,
  Link as LinkIcon,
  ExternalLink,
  Loader2,
  Upload,
  ImageIcon
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const categories = ['Therapy', 'Nutrition', 'Sports', 'Wellness', 'Health Tips'];

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const fileInputRef = useRef(null);
  const [currentPost, setCurrentPost] = useState(null);
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const emptyPost = {
    title: '',
    slug: '',
    category: 'Therapy',
    excerpt: '',
    content: '',
    featured_image: '',
    published: false
  };

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/blog`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const generateSlug = (title) => {
    return title.toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const openEditor = (post = null) => {
    setCurrentPost(post || { ...emptyPost });
    setIsEditing(true);
  };

  const closeEditor = () => {
    setIsEditing(false);
    setCurrentPost(null);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_URL}/api/admin/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentPost({ ...currentPost, featured_image: data.url });
        toast.success('Image uploaded');
      } else {
        toast.error('Failed to upload image');
      }
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleTitleChange = (title) => {
    const updates = { title };
    // Auto-generate slug if it's a new post or slug is empty
    if (!currentPost.id || !currentPost.slug) {
      updates.slug = generateSlug(title);
    }
    setCurrentPost({ ...currentPost, ...updates });
  };

  const handleSave = async () => {
    if (!currentPost.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!currentPost.content.trim()) {
      toast.error('Content is required');
      return;
    }

    setSaving(true);
    try {
      const isNew = !currentPost.id;
      const url = isNew 
        ? `${API_URL}/api/admin/blog`
        : `${API_URL}/api/admin/blog/${currentPost.id}`;
      
      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: currentPost.title,
          slug: currentPost.slug || null,
          category: currentPost.category,
          excerpt: currentPost.excerpt,
          content: currentPost.content,
          featured_image: currentPost.featured_image || null,
          published: currentPost.published
        })
      });

      if (response.ok) {
        await fetchPosts();
        closeEditor();
        toast.success(isNew ? 'Post created successfully' : 'Post updated successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save post');
      }
    } catch (error) {
      console.error('Failed to save post:', error);
      toast.error('Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/blog/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setPosts(posts.filter(p => p.id !== postId));
        toast.success('Post deleted successfully');
      } else {
        toast.error('Failed to delete post');
      }
    } catch (error) {
      console.error('Failed to delete post:', error);
      toast.error('Failed to delete post');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not published';
    return new Date(dateString).toLocaleDateString('en-IE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-sage-600 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-blog">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl font-light text-charcoal mb-2">Blog Management</h1>
          <p className="text-charcoal/60">Create and manage blog posts</p>
        </div>
        <Button
          onClick={() => openEditor()}
          className="bg-sage-600 hover:bg-sage-700 text-white rounded-xl gap-2"
          data-testid="new-post-btn"
        >
          <Plus className="w-5 h-5" />
          New Post
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <Card className="bg-white border-0 shadow-soft rounded-xl">
          <CardContent className="p-5 text-center">
            <div className="text-2xl font-serif text-charcoal">{posts.length}</div>
            <div className="text-sm text-charcoal/60">Total Posts</div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-0 shadow-soft rounded-xl">
          <CardContent className="p-5 text-center">
            <div className="text-2xl font-serif text-green-700">{posts.filter(p => p.published).length}</div>
            <div className="text-sm text-green-600">Published</div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-0 shadow-soft rounded-xl">
          <CardContent className="p-5 text-center">
            <div className="text-2xl font-serif text-amber-700">{posts.filter(p => !p.published).length}</div>
            <div className="text-sm text-amber-600">Drafts</div>
          </CardContent>
        </Card>
      </div>

      {/* Posts List */}
      {posts.length === 0 ? (
        <Card className="bg-white border-0 shadow-soft rounded-xl">
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 text-charcoal/20 mx-auto mb-4" />
            <p className="text-charcoal/60 mb-4">No blog posts yet</p>
            <Button onClick={() => openEditor()} className="bg-sage-600 hover:bg-sage-700 text-white rounded-xl">
              Create Your First Post
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {posts.map((post, index) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="bg-white border-0 shadow-soft hover:shadow-medium transition-all rounded-xl overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-charcoal">{post.title}</h3>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          post.published 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {post.published ? 'Published' : 'Draft'}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-charcoal/60 mb-2">
                        <span className="flex items-center gap-1">
                          <Tag className="w-4 h-4" />
                          {post.category}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(post.published_date || post.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <LinkIcon className="w-4 h-4" />
                          /resources/blog/{post.slug}
                        </span>
                      </div>
                      <p className="text-sm text-charcoal/60 line-clamp-1">
                        {post.excerpt}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      {post.published && (
                        <a
                          href={`/resources/blog/${post.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-charcoal/60 hover:text-sage-600 transition-colors"
                          title="View post"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditor(post)}
                        className="text-charcoal/60 hover:text-charcoal"
                        data-testid={`edit-post-${post.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deletePost(post.id)}
                        className="text-charcoal/60 hover:text-red-600"
                        data-testid={`delete-post-${post.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Editor Modal */}
      {isEditing && currentPost && (
        <div className="fixed inset-0 bg-charcoal/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-border/50 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="font-serif text-2xl text-charcoal">
                {currentPost.id ? 'Edit Post' : 'New Post'}
              </h2>
              <button
                onClick={closeEditor}
                className="p-2 hover:bg-cream-50 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Title *</label>
                <Input
                  value={currentPost.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="Post title"
                  className="h-11 bg-cream-50 border-border/50 rounded-xl"
                  data-testid="post-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">
                  Slug
                  <span className="text-charcoal/50 font-normal ml-2">/resources/blog/{currentPost.slug || 'your-slug'}</span>
                </label>
                <Input
                  value={currentPost.slug}
                  onChange={(e) => setCurrentPost({ ...currentPost, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                  placeholder="custom-url-slug"
                  className="h-11 bg-cream-50 border-border/50 rounded-xl"
                  data-testid="post-slug-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Category</label>
                  <Select
                    value={currentPost.category}
                    onValueChange={(value) => setCurrentPost({ ...currentPost, category: value })}
                  >
                    <SelectTrigger className="h-11 bg-cream-50 border-border/50 rounded-xl" data-testid="post-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Status</label>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCurrentPost({ ...currentPost, published: !currentPost.published })}
                    className={`w-full h-11 rounded-xl gap-2 ${
                      currentPost.published 
                        ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100' 
                        : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'
                    }`}
                    data-testid="post-status-toggle"
                  >
                    {currentPost.published ? (
                      <>
                        <Eye className="w-4 h-4" />
                        Published
                      </>
                    ) : (
                      <>
                        <EyeOff className="w-4 h-4" />
                        Draft
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Featured Image</label>
                {/* Image preview */}
                {currentPost.featured_image ? (
                  <div className="mb-3 relative rounded-xl overflow-hidden bg-cream-50 aspect-video" data-testid="post-image-preview">
                    <img 
                      src={currentPost.featured_image.startsWith('/api') ? `${API_URL}${currentPost.featured_image}` : currentPost.featured_image} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <button
                      type="button"
                      onClick={() => setCurrentPost({ ...currentPost, featured_image: '' })}
                      className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1.5 hover:bg-black/70 transition-colors"
                      data-testid="post-image-remove"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="mb-3 rounded-xl border-2 border-dashed border-border/50 bg-cream-50 h-32 flex flex-col items-center justify-center text-charcoal/40">
                    <ImageIcon className="w-8 h-8 mb-2" />
                    <span className="text-sm">No image selected</span>
                  </div>
                )}
                {/* Upload button */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="post-image-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full rounded-xl h-11 gap-2"
                  data-testid="post-image-upload-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      {currentPost.featured_image ? 'Replace Image' : 'Upload Image'}
                    </>
                  )}
                </Button>
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Excerpt</label>
                <Textarea
                  value={currentPost.excerpt}
                  onChange={(e) => setCurrentPost({ ...currentPost, excerpt: e.target.value })}
                  placeholder="Brief summary of the post (shown in listings)..."
                  rows={2}
                  className="bg-cream-50 border-border/50 rounded-xl resize-none"
                  data-testid="post-excerpt-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Content *</label>
                <Textarea
                  value={currentPost.content}
                  onChange={(e) => setCurrentPost({ ...currentPost, content: e.target.value })}
                  placeholder="Write your post content here..."
                  rows={12}
                  className="bg-cream-50 border-border/50 rounded-xl resize-none font-mono text-sm"
                  data-testid="post-content-input"
                />
                <p className="text-xs text-charcoal/50 mt-1">
                  Plain text content. Use blank lines to separate paragraphs.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-border/50 flex justify-end gap-3 sticky bottom-0 bg-white">
              <Button variant="outline" onClick={closeEditor} className="rounded-xl">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !currentPost.title || !currentPost.content}
                className="bg-sage-600 hover:bg-sage-700 text-white rounded-xl gap-2"
                data-testid="save-post-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Post
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
